import { isAxiosError } from 'axios'
import { Gift, Loader2 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

import { CatalogSelect } from '@/app/components/ui/CatalogSelect'
import { Button } from '@/app/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/app/components/ui/dialog'
import { createIdempotencyKey } from '@/app/lib/idempotency'
import { clientsService, type ClientItem } from '@/app/services/clients.service'
import { loyaltyService, type Reward } from '@/app/services/loyalty.service'

interface RedeemModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  selectedReward: Reward | null
  onSuccess?: () => void
}

const money = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' })

interface RedemptionApiError {
  message?: string
  errors?: Record<string, string[]>
}

export function RedeemModal({ open, onOpenChange, selectedReward, onSuccess }: RedeemModalProps) {
  const [customerId, setCustomerId] = useState(0)
  const [customer, setCustomer] = useState<ClientItem | null>(null)
  const [isLoadingCustomer, setIsLoadingCustomer] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const operationRef = useRef<{ context: string; key: string } | null>(null)

  useEffect(() => {
    if (!open) {
      setCustomerId(0)
      setCustomer(null)
      setIsSubmitting(false)
      operationRef.current = null
    }
  }, [open])

  useEffect(() => {
    operationRef.current = null
  }, [selectedReward?.id])

  useEffect(() => {
    if (!customerId) return
    let active = true
    setIsLoadingCustomer(true)
    clientsService.getItem(customerId)
      .then((value) => active && setCustomer(value))
      .catch(() => toast.error('No se pudo consultar el saldo del cliente'))
      .finally(() => active && setIsLoadingCustomer(false))
    return () => { active = false }
  }, [customerId])

  const points = customer?.loyalty?.pointsBalance ?? 0
  const canRedeem = Boolean(selectedReward && customer && points >= selectedReward.pointsRequired)

  const submit = async () => {
    if (!selectedReward || !customer || !canRedeem) return
    setIsSubmitting(true)
    try {
      const context = `${selectedReward.id}:${customer.id}`
      if (operationRef.current?.context !== context) {
        operationRef.current = { context, key: createIdempotencyKey() }
      }

      await loyaltyService.redeemReward(selectedReward.id, customer.id, operationRef.current.key)
      operationRef.current = null
      toast.success('Recompensa canjeada; el saldo quedó disponible para una compra futura')
      onOpenChange(false)
      onSuccess?.()
    } catch (caught: unknown) {
      if (isAxiosError<RedemptionApiError>(caught)) {
        const firstError = Object.values(caught.response?.data?.errors || {})[0]?.[0]
        toast.error(firstError || caught.response?.data?.message || 'No se pudo comunicar con el servidor para completar el canje')
      } else {
        toast.error('No se pudo preparar la solicitud de canje en este navegador')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !isSubmitting && onOpenChange(next)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Canjear Recompensa</DialogTitle>
          <DialogDescription className="sr-only">Convierte puntos del cliente en saldo de lealtad.</DialogDescription>
        </DialogHeader>
        {selectedReward && (
          <div className="space-y-4">
            <div className="rounded-xl bg-purple-50 p-6 text-center">
              <Gift className="mx-auto mb-3 h-12 w-12 text-purple-600" />
              <h3 className="font-semibold text-slate-900">{selectedReward.title}</h3>
              <p className="mt-2 text-3xl font-semibold text-purple-700">{selectedReward.pointsRequired.toLocaleString('es-MX')} puntos</p>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Buscar cliente:</label>
              <CatalogSelect
                catalog="customers"
                value={customerId || undefined}
                onChange={(value) => {
                  operationRef.current = null
                  setCustomer(null)
                  setCustomerId(value ? Number(value) : 0)
                }}
                placeholder="Nombre o teléfono del cliente..."
                itemLabelFormatter={(item) => item.phone ? `${item.name} · ${item.phone}` : item.name}
                disabled={isSubmitting}
              />
            </div>

            {isLoadingCustomer ? (
              <div className="flex items-center justify-center py-3 text-sm text-slate-500"><Loader2 className="mr-2 h-4 w-4 animate-spin" />Consultando saldo...</div>
            ) : customer ? (
              <div className="grid grid-cols-3 gap-2 rounded-lg border border-slate-200 p-3 text-center text-xs">
                <div><span className="block text-slate-500">Puntos</span><strong>{points.toLocaleString('es-MX')}</strong></div>
                <div><span className="block text-slate-500">Saldo actual</span><strong>{money.format(customer.loyalty?.creditBalance ?? 0)}</strong></div>
                <div><span className="block text-slate-500">Obtendrá</span><strong className="text-purple-700">{money.format(selectedReward.creditAmount)}</strong></div>
              </div>
            ) : null}

            <div className="rounded-lg bg-primary/10 p-3 text-sm text-primary/90">
              El cliente debe tener al menos {selectedReward.pointsRequired.toLocaleString('es-MX')} puntos disponibles.
              {customer && !canRedeem ? <span className="mt-1 block font-medium text-red-600">Puntos insuficientes.</span> : null}
            </div>

            <Button className="w-full" disabled={!canRedeem || isSubmitting || isLoadingCustomer} onClick={() => void submit()}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Confirmar Canje
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
